import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Menu,
  MenuItem,
  Popover,
  SvgIconProps,
  Typography,
} from "@mui/material";
import filtersStyles from "../SignRequestsFilters.styles";
import {
  getClearedFilter,
  getDateFilterLabel,
  getFilterOperator,
  getFilterValueCount,
  SIGN_REQUESTS_STATUS_OPTIONS,
  SignRequestsFilterKey,
  SignRequestsFilterLookups,
  SignRequestsFilters,
  SignRequestsOption,
} from "../SignRequestsFiltersHelper";
import signRequestsFiltersTokens, {
  SignRequestsTagColor,
} from "../SignRequestsFilters.tokens";
import { getUiStatusTone } from "../SignRequestsRowHelper";
import SignRequestsFilterPanel from "./SignRequestsFilterPanel";
import {
  BookmarkGlyph,
  CalendarGlyph,
  ClockGlyph,
  CloseGlyph,
  FilterLinesGlyph,
  PencilGlyph,
  PersonGlyph,
  PlusGlyph,
  TagGlyph,
  TemplateGlyph,
} from "./SignRequestsIcons";

const styles = filtersStyles;
const { filterBar } = signRequestsFiltersTokens;

/** Values drawn inside a pill before the rest collapse into "+N". */
const MAX_PILL_VALUES = 2;

export const FILTER_CATEGORY_ICONS: Record<
  SignRequestsFilterKey,
  (props: SvgIconProps) => JSX.Element
> = {
  status: ClockGlyph,
  date: CalendarGlyph,
  signer: PencilGlyph,
  senders: PersonGlyph,
  templates: TemplateGlyph,
  tags: TagGlyph,
};

interface PillPart {
  label: string;
  /** A tag keeps its chip colours. */
  color?: SignRequestsTagColor;
  /** A status carries its dot. */
  dot?: string;
}

const labelOf = (value: string, options: SignRequestsOption[]) =>
  options.find((option) => option.value === value)?.label ?? value;

/** What a pill shows for its filter's value. */
const getPillParts = (
  filters: SignRequestsFilters,
  key: SignRequestsFilterKey,
  lookups: SignRequestsFilterLookups,
): PillPart[] => {
  switch (key) {
    case "status":
      return filters.status.map((status) => ({
        label: labelOf(status, SIGN_REQUESTS_STATUS_OPTIONS),
        dot: getUiStatusTone(status).dot,
      }));
    case "date": {
      const label = getDateFilterLabel(filters);
      return label ? [{ label }] : [];
    }
    case "signer":
      return filters.signer.trim()
        ? [{ label: `“${filters.signer.trim()}”` }]
        : [];
    case "senders":
      return filters.senders.map((value) => ({
        label: labelOf(value, lookups.senders),
      }));
    case "templates":
      return filters.templates.map((value) => ({
        label: labelOf(value, lookups.templates),
      }));
    default:
      return filters.tags
        .map((id) => lookups.tags.find((tag) => tag.id === id))
        .filter((tag): tag is NonNullable<typeof tag> => Boolean(tag))
        .map((tag) => ({ label: tag.name, color: tag.color }));
  }
};

interface SignRequestsFilterBarProps {
  filters: SignRequestsFilters;
  /** The filters on offer — Sender is absent for a user who sees only their own. */
  categories: { key: SignRequestsFilterKey; label: string }[];
  lookups: SignRequestsFilterLookups;
  disabled: boolean;
  onApply: (filters: SignRequestsFilters) => void;
  onClearAll: () => void;
  onOpenAllFilters: () => void;
  /** Filters are applied but belong to no saved view yet. */
  canSaveView: boolean;
  onSaveView: () => void;
  isLoadingTags: boolean;
  isLoadingOptions: boolean;
  onManageTags: () => void;
  onCreateFirstTag: () => void;
}

/**
 * The row under the toolbar. Each applied filter is a pill — label, operator,
 * value, remove — whose value opens that filter's editor in a popover; "Add
 * filter" starts a new one, and "All filters" opens the drawer.
 *
 * A popover edits a draft and only Apply commits it, so clicking away is a
 * cancel. Only one popover is open at a time.
 */
const SignRequestsFilterBar = ({
  filters,
  categories,
  lookups,
  disabled,
  onApply,
  onClearAll,
  onOpenAllFilters,
  canSaveView,
  onSaveView,
  isLoadingTags,
  isLoadingOptions,
  onManageTags,
  onCreateFirstTag,
}: SignRequestsFilterBarProps) => {
  const [openKey, setOpenKey] = useState<SignRequestsFilterKey | null>(null);
  const [draft, setDraft] = useState<SignRequestsFilters>(filters);
  /*
    Each popover hangs off its own pill. A pill for a filter that was just
    added does not exist until the render that opens it, so the anchor is
    picked up in an effect, one render later.
  */
  const pillRefs = useRef<
    Partial<Record<SignRequestsFilterKey, HTMLElement | null>>
  >({});
  const [panelAnchor, setPanelAnchor] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPanelAnchor(openKey ? (pillRefs.current[openKey] ?? null) : null);
  }, [openKey]);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);

  const openPanel = (key: SignRequestsFilterKey) => {
    setDraft(filters);
    setOpenKey(key);
  };
  const closePanel = () => setOpenKey(null);

  const shown = categories.filter(
    (category) =>
      getFilterValueCount(filters, category.key) > 0 ||
      openKey === category.key,
  );
  const addable = categories.filter(
    (category) => !shown.some((entry) => entry.key === category.key),
  );
  const hasActive = categories.some(
    (category) => getFilterValueCount(filters, category.key) > 0,
  );
  const openCategory = categories.find((category) => category.key === openKey);

  return (
    <Box sx={styles.filterBar}>
      {shown.map((category) => {
        const Icon = FILTER_CATEGORY_ICONS[category.key];
        const open = openKey === category.key;
        const parts = getPillParts(filters, category.key, lookups);
        const visibleParts = parts.slice(0, MAX_PILL_VALUES);
        const hiddenCount = parts.length - visibleParts.length;

        return (
          <Box
            key={category.key}
            ref={(element: HTMLElement | null) => {
              pillRefs.current[category.key] = element;
            }}
            sx={styles.filterPill(open)}
          >
            <Box component="span" sx={styles.filterPillLabel}>
              <Icon sx={styles.filterPillGlyph} />
              {category.label}
            </Box>
            <Box component="span" sx={styles.filterPillOperator}>
              {getFilterOperator(filters, category.key)}
            </Box>
            <ButtonBase
              sx={styles.filterPillValue(open)}
              disabled={disabled}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-label={`Edit ${category.label} filter`}
              onClick={() => (open ? closePanel() : openPanel(category.key))}
            >
              {visibleParts.map((part) => (
                <Box
                  key={part.label}
                  component="span"
                  sx={styles.filterPillPart(part.color)}
                >
                  {part.dot && (
                    <Box component="span" sx={styles.filterPillDot(part.dot)} />
                  )}
                  {part.label}
                </Box>
              ))}
              {hiddenCount > 0 && (
                <Box component="span" sx={styles.filterPillMore}>
                  +{hiddenCount}
                </Box>
              )}
              {parts.length === 0 && (
                <Box component="span" sx={styles.filterPillEmpty}>
                  Choose…
                </Box>
              )}
            </ButtonBase>
            <ButtonBase
              sx={styles.filterPillRemove}
              disabled={disabled}
              aria-label={`Remove ${category.label} filter`}
              onClick={() => {
                if (open) closePanel();
                // A pill that was only just added holds nothing to remove.
                if (parts.length > 0) {
                  onApply({ ...filters, ...getClearedFilter(category.key) });
                }
              }}
            >
              <CloseGlyph sx={styles.filterPillRemoveGlyph} />
            </ButtonBase>
          </Box>
        );
      })}

      <ButtonBase
        sx={styles.filterAdd}
        disabled={disabled || addable.length === 0}
        aria-haspopup="menu"
        aria-expanded={Boolean(addAnchor)}
        onClick={(event) => setAddAnchor(event.currentTarget)}
      >
        <PlusGlyph sx={styles.filterAddGlyph} />
        {shown.length ? "Add filter" : "Filter"}
      </ButtonBase>
      <Menu
        anchorEl={addAnchor}
        open={Boolean(addAnchor)}
        onClose={() => setAddAnchor(null)}
        PaperProps={{ sx: styles.filterAddMenuPaper }}
      >
        <Box sx={styles.menuSectionLabel}>Filter by</Box>
        {addable.map((category) => {
          const Icon = FILTER_CATEGORY_ICONS[category.key];
          return (
            <MenuItem
              key={category.key}
              sx={styles.filterAddItem}
              onClick={() => {
                setAddAnchor(null);
                openPanel(category.key);
              }}
            >
              <Icon sx={styles.filterPillGlyph} />
              {category.label}
            </MenuItem>
          );
        })}
      </Menu>

      <ButtonBase
        sx={styles.filterBarLink(filterBar.linkColor)}
        disabled={disabled}
        aria-haspopup="dialog"
        onClick={onOpenAllFilters}
      >
        <FilterLinesGlyph sx={styles.filterBarLinkGlyph} />
        All filters
      </ButtonBase>

      <Box sx={styles.filterBarSpacer} />

      {canSaveView && (
        <ButtonBase
          sx={styles.filterBarLink(filterBar.saveColor)}
          disabled={disabled}
          onClick={onSaveView}
        >
          <BookmarkGlyph sx={styles.filterBarLinkGlyph} />
          Save view
        </ButtonBase>
      )}
      {hasActive && (
        <Button
          size="small"
          variant="text"
          disabled={disabled}
          onClick={onClearAll}
        >
          Clear all
        </Button>
      )}

      <Popover
        open={Boolean(openCategory && panelAnchor)}
        anchorEl={panelAnchor}
        onClose={closePanel}
        // The custom-range calendar opens in a layer of its own, with its own
        // focus trap; this one must not pull focus back out of it.
        disableEnforceFocus
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        PaperProps={{
          sx: styles.panelPopover,
          role: "dialog",
          "aria-label": openCategory ? `${openCategory.label} filter` : "",
        }}
      >
        {openCategory && (
          <>
            <Box sx={styles.panelHead}>
              <Typography component="h3" sx={styles.panelTitle}>
                {openCategory.label}
              </Typography>
              {getFilterValueCount(draft, openCategory.key) > 0 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    setDraft((previous) => ({
                      ...previous,
                      ...getClearedFilter(openCategory.key),
                    }))
                  }
                >
                  Clear
                </Button>
              )}
            </Box>
            <Box sx={styles.panelBody}>
              <SignRequestsFilterPanel
                // Remounted per category so its search and date mode start clean.
                key={openCategory.key}
                category={openCategory.key}
                draft={draft}
                onChange={(change) =>
                  setDraft((previous) => ({ ...previous, ...change }))
                }
                lookups={lookups}
                isLoadingTags={isLoadingTags}
                isLoadingOptions={isLoadingOptions}
                onManageTags={() => {
                  closePanel();
                  onManageTags();
                }}
                onCreateFirstTag={() => {
                  closePanel();
                  onCreateFirstTag();
                }}
              />
            </Box>
            <Box sx={styles.panelFoot}>
              <Button size="small" variant="outlined" onClick={closePanel}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  closePanel();
                  onApply(draft);
                }}
              >
                Apply
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </Box>
  );
};

export default SignRequestsFilterBar;
